import * as React from 'react'
import { observer } from 'mobx-react'
import { JobItemViewModel } from './JobItemViewModel'
import { useCallRootViewModel } from 'contexts/CallRootContext'
import { isNotNil } from 'utils/object-util'
import link from 'infra/link'
import Avatar from 'components/Avatar'
import Icon from 'components/Icon'
import UnreadBadge from 'components/UnreadBadge'
import {
  SplitViewSidebarListItem,
  SplitViewSidebarListItemTitle,
  SplitViewSidebarListItemSubtitle,
  SplitViewSidebarListItemPretitle,
} from 'components/SplitView'
import styles from './JobItem.sass'

interface JobItemProps {
  vm: JobItemViewModel
}

export default observer(function JobItem({ vm }: JobItemProps) {
  const collaboratorNames = getCollaboratorNames(vm)
  const activeCall = useCallRootViewModel().activeCall
  const meta = activeCall?.call.conversation.meta
  const jobIsCallSource = meta?.type === 'Job' && vm.job.id === meta.id
  const cameraOn =
    jobIsCallSource &&
    Boolean(activeCall?.currentUserParticipant?.media?.camera)

  return (
    <SplitViewSidebarListItem
      to={vm.job.id ? link.job(vm.job.id) : ''}
      active={vm.isActive}
      visual={
        jobIsCallSource ? (
          <div className={styles.ongoingCall}>
            {cameraOn ? (
              <Icon size={18} name="call-video-on" />
            ) : (
              <Icon size={18} rotate={-135} name="call-end-call" />
            )}
          </div>
        ) : (
          <Avatar
            exclaim={!!vm.latestOpenTicket}
            size={32}
            subject={vm.collaborators.map((c) => c)}
          />
        )
      }
      extras={
        <div className={styles.stats}>
          <span className={styles.lastActivity}>{vm.lastActivity}</span>
          {vm.unreadCount > 0 && (
            <span className={styles.unread}>
              <UnreadBadge count={vm.unreadCount} />
            </span>
          )}
        </div>
      }
    >
      {vm.isWorkspaceInfoRelevant && (
        <SplitViewSidebarListItemPretitle title={vm.job.workspace.name}>
          {vm.job.workspace.name}
        </SplitViewSidebarListItemPretitle>
      )}
      <SplitViewSidebarListItemTitle title={vm.jobName}>
        {vm.jobName}
      </SplitViewSidebarListItemTitle>
      <SplitViewSidebarListItemSubtitle title={collaboratorNames}>
        {collaboratorNames}
      </SplitViewSidebarListItemSubtitle>
    </SplitViewSidebarListItem>
  )
})

function getCollaboratorNames(vm: JobItemViewModel) {
  return (
    vm.collaborators
      .map((c) => c.displayName)
      .filter(isNotNil)
      .join(', ') || 'just you'
  )
}
